import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Package, Download, Trash2, FileText, Calendar, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
const fmtDate = (s) => {
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export default function Archive() {
  const [groups, setGroups] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // id -> "downloading" | "deleting"

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/archive`, { withCredentials: true });
      setGroups(data?.groups || []);
      setCount(data?.count || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Archive");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const refresh = () => fetchData();
    window.addEventListener("archive:changed", refresh);
    return () => window.removeEventListener("archive:changed", refresh);
  }, [fetchData]);

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const handleDownload = async (item) => {
    setBusyFor(item.id, "downloading");
    try {
      const r = await axios.get(`${API}/archive/${item.id}/download`, {
        responseType: "blob",
        withCredentials: true,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = item.filename || "archive.pdf";
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Download failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusyFor(item.id, null);
    }
  };

  const handleView = async (item) => {
    setBusyFor(item.id, "downloading");
    try {
      const r = await axios.get(`${API}/archive/${item.id}/download`, {
        responseType: "blob",
        withCredentials: true,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast.error("Preview failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusyFor(item.id, null);
    }
  };

  const handlePrint = async (item) => {
    setBusyFor(item.id, "downloading");
    try {
      const r = await axios.get(`${API}/archive/${item.id}/download`, {
        responseType: "blob",
        withCredentials: true,
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(blobUrl, "_blank");
      // Best-effort: trigger print dialog once the PDF renders
      if (w) {
        w.addEventListener("load", () => { try { w.print(); } catch { /* ignore */ } });
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast.error("Print failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusyFor(item.id, null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(
      `⚠️ IRS recommends keeping tax records for 7 years.\n\nDelete "${item.title}"?\n\nThis cannot be undone.`
    )) return;
    setBusyFor(item.id, "deleting");
    try {
      await axios.delete(`${API}/archive/${item.id}`, { withCredentials: true });
      toast.success("Archive deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusyFor(item.id, null);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="archive-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                Archive
              </h1>
              <p className="text-slate-500 text-sm">
                Permanent PDF copies of reports you've saved. Grouped by Tax Year.
              </p>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total Archives</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="archive-total-count">{count}</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          Click the <strong>📦 Archive ▾</strong> button on any report page (Tax Prep, Transactions, CACFP,
          Income, Expenses, Grant Tracking, Equipment) to save a permanent PDF snapshot here.
          Archives never change — they're locked at the moment you saved them.
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="archive-empty-state">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-700 font-semibold mb-1">No archived reports yet</p>
          <p className="text-sm text-slate-500">
            Generate a report (Tax Prep, CACFP, Income, Expenses, Grants, Equipment) and click the
            amber <strong>📦 Archive ▾</strong> button to save it here.
          </p>
        </div>
      )}

      {!loading && groups.map((g) => (
        <div key={g.tax_year} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`archive-group-${g.tax_year}`}>
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>
                Tax Year {g.tax_year}
              </h2>
              <span className="text-xs text-slate-500">({g.items.length} archive{g.items.length === 1 ? "" : "s"})</span>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {g.items.map((it) => (
              <div key={it.id} className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`archive-item-${it.id}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-amber-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 text-sm truncate" title={it.title}>
                      {it.title}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      <span className="font-mono">{it.filename}</span>
                      <span className="mx-1.5">·</span>
                      {fmtBytes(it.pdf_size)}
                      <span className="mx-1.5">·</span>
                      {fmtDate(it.created_at)}
                    </p>
                    {it.optional_note && (
                      <p className="text-xs text-slate-600 mt-1 italic">"{it.optional_note}"</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold uppercase tracking-wider mr-1">
                        {it.category}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => handleView(it)}
                    disabled={busy[it.id]}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`archive-view-${it.id}`}
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDownload(it)}
                    disabled={busy[it.id]}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`archive-download-${it.id}`}
                  >
                    {busy[it.id] === "downloading"
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Download className="w-3 h-3" />}
                    Download
                  </button>
                  <button
                    onClick={() => handlePrint(it)}
                    disabled={busy[it.id]}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`archive-print-${it.id}`}
                  >
                    Print
                  </button>
                  <button
                    onClick={() => handleDelete(it)}
                    disabled={busy[it.id]}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`archive-delete-${it.id}`}
                  >
                    {busy[it.id] === "deleting"
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Trash2 className="w-3 h-3" />}
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
